import { useEffect } from 'react';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useTranslation, Trans } from 'react-i18next';
import { MailtoLink } from '@app/components';
import { EMAILS } from '@app/constants';
import { useTokenUserData } from '@data/hooks';
import { Namespace } from '@data/index';
import { useOnboardingsStore } from '@data/storage';
import { AntdTypography, Button, AntdRow, AntdCol } from '@ui';
import PanelImage from './assets/panel.webp';

const { Title, Text, Paragraph } = AntdTypography;

const PanelLanding = () => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const { userId } = useTokenUserData();
  const { onboardings, setOnboardingStepCompleted } = useOnboardingsStore();

  useEffect(() => {
    const panelStep = onboardings[userId]?.steps?.panel;

    if (panelStep) {
      if (!panelStep.completed) {
        setOnboardingStepCompleted(userId, 'panel');
      }
    }
  }, [userId]);

  return (
    <>
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex-1">
            <Title level={2} className="text-2xl w-full m-0 mb-4" data-testid="panel-landing-title">
              {t('panel_landing.title')}
            </Title>
          </div>
          <MailtoLink
            email={EMAILS.CONTACT_CUREWIKI_HEALTH}
            subjectKey="team_settings.email_subjects.panel_access"
            bodyKey="team_settings.email_bodies.panel_access"
          >
            <Button
              type="primary"
              size="large"
              className="bg-success border-success hover:bg-success hover:border-success px-6 py-3 h-auto"
              icon={<ArrowRightOutlined />}
              iconPlacement="end"
            >
              {t('panel_landing.access_button')}
            </Button>
          </MailtoLink>
        </div>
        <Paragraph className="text-base-600">{t('panel_landing.premium_feature')}</Paragraph>
        <Paragraph className="text-base text-base-900 mb-6">
          {t('panel_landing.description')}
        </Paragraph>
      </div>
      <AntdRow gutter={[0, 16]} className="mb-8">
        <AntdCol span={24}>
          <ul className="pl-2">
            <li className="flex items-start">
              <span className="w-2 h-2 bg-base-900 rounded-full mt-3 mr-4 flex-shrink-0"></span>
              <Text className="text-base text-base-800">
                {t('panel_landing.feature_completion_rate')}
              </Text>
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-base-900 rounded-full mt-3 mr-4 flex-shrink-0"></span>
              <Text className="text-base text-base-800">
                {t('panel_landing.feature_targeting')}
              </Text>
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-base-900 rounded-full mt-3 mr-4 flex-shrink-0"></span>
              <Text className="text-base text-base-800">{t('panel_landing.feature_reports')}</Text>
            </li>
          </ul>
        </AntdCol>
      </AntdRow>

      <div className="font-semibold">
        <Text className="text-base text-base-800 block mb-2">
          {t('panel_landing.premium_offer_title')}
        </Text>
        <div className="flex items-center gap-2">
          <ArrowRightOutlined className="text-base text-base-800" />
          <Text className="text-base text-base-800">
            <Trans
              i18nKey="panel_landing.premium_offer_contact"
              ns={Namespace.ADMIN}
              components={{
                1: (
                  <MailtoLink
                    email={EMAILS.CONTACT_CUREWIKI_HEALTH}
                    subjectKey="team_settings.email_subjects.panel_access"
                    bodyKey="team_settings.email_bodies.panel_access"
                    className="text-primary-600 font-medium"
                  />
                ),
              }}
            />
          </Text>
        </div>
      </div>

      <div className="mt-12 flex justify-center">
        <img
          src={PanelImage}
          alt="Panel feature"
          className="w-full max-w-4xl h-auto rounded-lg shadow-lg"
          loading="lazy"
          decoding="async"
        />
      </div>
    </>
  );
};

export default PanelLanding;
